import React from 'react';
import { Facebook, Twitter, Instagram, Youtube, Linkedin, ExternalLink, Mail, Calendar, Moon } from 'lucide-react';

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  const goAfterDark = () => {
    const el = document.getElementById('afterdark');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const footerLinks = {
    explore: [
      { name: 'About', href: '#about' },
      { name: 'Literary Works', href: '#books' },
      { name: 'Music', href: '#music' },
      { name: 'Four Pillars', href: '#pillars' }
    ],
    resources: [
      { name: 'Blog', href: '#' },
      { name: 'Podcast', href: '#' },
      { name: 'Events', href: '#' },
      { name: 'Media Kit', href: '#' }
    ],
    legal: [
      { name: 'Privacy Policy', href: '#' },
      { name: 'Terms of Service', href: '#' },
      { name: 'Cookie Policy', href: '#' },
      { name: 'Copyright', href: '#' }
    ]
  };

  const socialLinks = [
    { icon: Facebook, href: '#', label: 'Facebook' },
    { icon: Twitter, href: '#', label: 'Twitter' },
    { icon: Instagram, href: '#', label: 'Instagram' },
    { icon: Youtube, href: '#', label: 'YouTube' },
    { icon: Linkedin, href: '#', label: 'LinkedIn' }
  ];

  return (
    <footer className="bg-gunmetal-950 border-t border-steel-800/50">
      {/* Official Launch Banner */}
      <div className="bg-gradient-to-r from-accent-red/20 via-accent-red/30 to-accent-red/20 border-b border-accent-red/30 py-3">
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-center space-x-3">
          <Calendar className="text-accent-red" size={18} />
          <p className="text-steel-200 text-sm">
            <span className="font-bold text-accent-red">OFFICIAL LAUNCH:</span> July 17, 2026
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Main Footer Content */}
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-8 mb-12">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <h3 className="text-2xl font-black text-steel-100 mb-4">
              MINDESIGN <span className="text-accent-red">ENTERPRISES</span>
            </h3>
            <p className="text-steel-400 mb-6 leading-relaxed max-w-md">
              Empowering minds and designing legacies through transformative literature, 
              original music, and practical wisdom.
            </p>
            <p className="text-accent-red font-bold text-lg italic mb-4">
              Your Life, Redesigned.
            </p>
            
            {/* Contact Email */}
            <a 
              href="mailto:info@mindesignenterprises.com"
              className="inline-flex items-center space-x-2 text-steel-300 hover:text-accent-red transition-colors group"
            >
              <Mail size={18} className="group-hover:scale-110 transition-transform" />
              <span className="font-medium">info@mindesignenterprises.com</span>
            </a>
            
            {/* Social Links */}
            <div className="flex space-x-4 mt-6">
              {socialLinks.map((social) => {
                const IconComponent = social.icon;
                return (
                  <a
                    key={social.label}
                    href={social.href}
                    aria-label={social.label}
                    className="w-10 h-10 bg-gunmetal-900 border border-steel-700/30 rounded-lg flex items-center justify-center text-steel-400 hover:bg-accent-red hover:text-white hover:border-accent-red transition-all duration-300 hover:scale-110"
                  >
                    <IconComponent size={18} />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Explore Links */}
          <div>
            <h4 className="text-steel-200 font-bold mb-4 uppercase tracking-wider text-sm">
              Explore
            </h4>
            <ul className="space-y-3">
              {footerLinks.explore.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    className="text-steel-400 hover:text-accent-red transition-colors duration-300 text-sm flex items-center group"
                  >
                    <span className="group-hover:translate-x-1 transition-transform duration-300">
                      {link.name}
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources Links */}
          <div>
            <h4 className="text-steel-200 font-bold mb-4 uppercase tracking-wider text-sm">
              Resources
            </h4>
            <ul className="space-y-3">
              {footerLinks.resources.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    className="text-steel-400 hover:text-accent-red transition-colors duration-300 text-sm flex items-center group"
                  >
                    <span className="group-hover:translate-x-1 transition-transform duration-300">
                      {link.name}
                    </span>
                    <ExternalLink size={12} className="ml-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h4 className="text-steel-200 font-bold mb-4 uppercase tracking-wider text-sm">
              Legal
            </h4>
            <ul className="space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    className="text-steel-400 hover:text-accent-red transition-colors duration-300 text-sm flex items-center group"
                  >
                    <span className="group-hover:translate-x-1 transition-transform duration-300">
                      {link.name}
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-steel-800/50 pt-8">
          {/* Spun Fun cross-link pill */}
          <div className="flex justify-center mb-6">
            <button
              type="button"
              onClick={goAfterDark}
              className="group inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-pink-500/10 via-fuchsia-500/10 to-purple-500/10 border border-pink-500/30 hover:border-pink-400/70 hover:bg-pink-500/15 transition-all duration-300 hover:shadow-[0_0_30px_rgba(236,72,153,0.3)]"
              data-testid="footer-afterdark-pill"
            >
              <Moon size={14} className="text-pink-400 group-hover:rotate-12 transition-transform" />
              <span className="text-[11px] tracking-[0.3em] uppercase font-bold text-pink-300 group-hover:text-pink-200">
                Visit Spun Fun · After Dark
              </span>
              <span className="text-pink-400/70 text-[10px]">18+</span>
            </button>
          </div>

          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            {/* Copyright */}
            <p className="text-steel-500 text-sm text-center md:text-left">
              © {currentYear} MINDESIGN ENTERPRISES. All rights reserved. 
              <span className="block md:inline md:ml-2">
                Founded by Jose Francisco Rodriguez Jr.
              </span>
            </p>

            {/* Additional Info */}
            <p className="text-steel-600 text-xs">
              Crafted with fierce creativity and relentless purpose
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};
